import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from './AuthContext';
import { customerService } from '../services/customerService';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';
import { logActivity } from '../utils/activityLogger';

const toCustomer = (row) => ({
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    phone: row.phone,
    birthday: row.birthday,
    tenantId: row.tenant_id,
    shortId: row.short_id,
    points: row.points,
    totalPoints: row.total_points,
    pointEarnings: row.point_earnings || [],
    interactionCount: row.interaction_count,
    purchaseCount: row.purchase_count,
    totalSpent: row.total_spent,
    visitTimestamps: row.visit_timestamps || [],
    loyaltyLevel: row.loyalty_level,
    visitRole: row.visit_role,
    fcmTokensList: row.fcm_tokens_list || [],
    fcmTokens: row.fcm_tokens || [],
    notificationsEnabled: row.notifications_enabled,
    createdBy: row.created_by,
    createdAt: row.created_at,
    lastInteractionAt: row.last_interaction_at
});

const toInteraction = (row) => ({
    id: row.id,
    customerId: row.customer_id,
    tenantId: row.tenant_id,
    userId: row.user_id,
    type: row.type,
    content: row.content,
    points: row.points,
    amount: row.amount,
    isAuto: row.is_auto,
    timestamp: row.timestamp
});

const CustomerContext = createContext();

export const useCustomers = () => useContext(CustomerContext);

export const CustomerProvider = ({ children }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { userData, user, tenantData, effectiveTenantId } = useAuth();

    useEffect(() => {
        if (!effectiveTenantId && !userData?.isSuperAdmin) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        // Initial fetch
        const fetchCustomers = async () => {
            let query = supabase.from('customers').select('*');

            if (effectiveTenantId) {
                query = query.eq('tenant_id', effectiveTenantId);
            } else if (userData?.isSuperAdmin) {
                query = query.order('created_at', { ascending: false }).limit(1000);
            }

            const { data, error } = await query;

            if (error) {
                console.error("Customer Fetch Error:", error);
                // If auth error, try refreshing the session
                if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
                    console.warn('[Customers] Auth token may be expired, triggering refresh...');
                    await supabase.auth.refreshSession();
                }
                if (!cancelled) setLoading(false);
                return;
            }

            if (!cancelled) {
                const docs = (data || []).map(toCustomer);
                docs.sort((a, b) => {
                    const getVal = (d) => d ? (d.toDate ? d.toDate() : new Date(d)) : 0;
                    return getVal(b.createdAt) - getVal(a.createdAt);
                });
                setCustomers(docs);
                setLoading(false);
            }
        };

        fetchCustomers();

        // Realtime subscription
        const channelFilter = effectiveTenantId
            ? `tenant_id=eq.${effectiveTenantId}`
            : undefined;

        const channelConfig = {
            event: '*',
            schema: 'public',
            table: 'customers'
        };
        if (channelFilter) {
            channelConfig.filter = channelFilter;
        }

        const channel = supabase.channel('customers-changes')
            .on('postgres_changes', channelConfig, (payload) => {
                if (cancelled) return;

                if (payload.eventType === 'INSERT') {
                    const newCustomer = toCustomer(payload.new);
                    setCustomers(prev => {
                        const updated = [newCustomer, ...prev];
                        updated.sort((a, b) => {
                            const getVal = (d) => d ? (d.toDate ? d.toDate() : new Date(d)) : 0;
                            return getVal(b.createdAt) - getVal(a.createdAt);
                        });
                        return updated;
                    });
                } else if (payload.eventType === 'UPDATE') {
                    const updatedCustomer = toCustomer(payload.new);
                    setCustomers(prev =>
                        prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c)
                    );
                } else if (payload.eventType === 'DELETE') {
                    const deletedId = payload.old.id;
                    setCustomers(prev => prev.filter(c => c.id !== deletedId));
                }
            })
            .subscribe();

        return () => {
            cancelled = true;
            supabase.removeChannel(channel);
        };
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    const addCustomer = async (customerData) => {
        return await customerService.addCustomer({
            customerData,
            tenantId: effectiveTenantId,
            userId: user.uid,
            userEmail: userData?.email || 'unknown',
            tenantData,
            currentCustomerCount: (customers || []).length
        });
    };

    const updateCustomer = async (id, updates) => {
        return await customerService.updateCustomer(id, updates, effectiveTenantId, user.uid, userData?.email || 'unknown');
    };

    const deleteCustomer = async (id) => {
        return await customerService.deleteCustomer(id, effectiveTenantId, user.uid, userData?.email || 'unknown');
    };

    const logInteraction = async (customerId, interactionData) => {
        return await customerService.logInteraction({
            customerId,
            interactionData,
            tenantId: effectiveTenantId,
            userId: user.uid,
            userEmail: userData?.email || 'unknown',
            tenantData
        });
    };

    const updateCustomerPoints = async (id, amount) => {
        return await customerService.updatePoints({
            customerId: id,
            amount,
            tenantId: effectiveTenantId,
            userId: user.uid,
            userEmail: userData?.email || 'unknown',
            tenantData
        });
    };

    const getInteractions = (customerId, callback) => {
        if (!effectiveTenantId) return () => { };

        const fetchAll = async () => {
            try {
                const [orderRes] = await Promise.all([
                    supabase.from('orders').select('*')
                        .eq('customer_id', customerId).eq('tenant_id', effectiveTenantId)
                ]);

                const combined = [
                    ...(orderRes.data || []).map(o => ({
                        id: o.id,
                        customerId: o.customer_id,
                        tenantId: o.tenant_id,
                        type: 'Purchase',
                        content: o.items?.length > 0 ? o.items.map(i => `${i.name} (${i.quantity}x)`).join(', ') : 'Satın Alma',
                        points: o.earned_points || 0,
                        amount: o.total_amount,
                        isAuto: false,
                        timestamp: o.timestamp || o.created_at
                    }))
                ];

                combined.sort((a, b) => {
                    const dateA = a.timestamp ? new Date(a.timestamp) : 0;
                    const dateB = b.timestamp ? new Date(b.timestamp) : 0;
                    return dateB - dateA;
                });
                callback(combined);
            } catch (err) {
                console.error("Fetch Combined Interactions Error:", err);
                callback([], err);
            }
        };

        fetchAll();

        const channel = supabase.channel(`combined-history-${customerId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `customer_id=eq.${customerId}` }, fetchAll)
            .subscribe();

        return () => supabase.removeChannel(channel);
    };

    const syncAllCustomersMetadata = async () => {
        if (!effectiveTenantId || (customers || []).length === 0) return 0;

        let updatedCount = 0;
        for (const customer of customers) {
            const levelObj = calculateLoyaltyLevel(customer, tenantData);
            const roleObj = calculateVisitFrequencyRole(customer, tenantData);
            const loyaltyLevel = levelObj?.label || null;
            const visitRole = roleObj?.label || null;

            if (customer.loyaltyLevel !== loyaltyLevel || customer.visitRole !== visitRole) {
                const updatePayload = {
                    loyalty_level: loyaltyLevel,
                    visit_role: visitRole
                };

                const { error } = await supabase
                    .from('customers')
                    .update(updatePayload)
                    .eq('id', customer.id);

                if (!error) updatedCount++;
            }
        }
        await logActivity(effectiveTenantId, 'system', user.uid, 'SYNC_METADATA', `Synced metadata for ${updatedCount} customers`);
        return updatedCount;
    };


    return (
        <CustomerContext.Provider value={{
            customers,
            loading,
            addCustomer,
            updateCustomer,
            deleteCustomer,
            logInteraction,
            getInteractions,
            syncAllCustomersMetadata,
            updateCustomerPoints,
            updateInteraction: async (id, updates) => {
                return await customerService.updateInteraction(id, updates, effectiveTenantId, user.uid, userData?.email || 'unknown');
            }
        }}>
            {children}
        </CustomerContext.Provider>
    );
};
